import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import FadeIn from "./FadeIn";
import { replayClass, scrollToAnchor } from "./scrollToAnchor";
import { motionMs } from "./tokens";
import { crossFadeOn, mountGhost, snapshotNode } from "./useCrossFade";

type Props = React.HTMLAttributes<HTMLDivElement> & {
  /** A change replays the fade and holds the old height; first mount does neither. */
  swapKey: unknown;
  /** A change leaves the old body as a ghost over the new one, with no replay; from an empty body, the fade. */
  crossFade?: boolean;
  /** A change holds the old height like a swapKey change, with no replay or ghost. */
  holdKey?: unknown;
  /** Focus the first visible heading of the new body, without scrolling. */
  focusHeading?: boolean;
  /** A visually hidden h2 naming the body, so focus lands on a heading on every tab (QA 2026-10-01). */
  heading?: string;
  /** Brought into view on a swap, only when it is offscreen (the tab strip). */
  revealId?: string;
};

type Hold = { px: number; easing: boolean };
type Snap =
  | { node: HTMLElement; top: number; height: number; scrollTop: number }
  | "empty"
  | null;

const hasContent = (el: HTMLElement) =>
  el.offsetHeight > 0 &&
  Array.from(el.children).some((c) => !c.classList.contains("sr-only"));

/**
 * Captures the body in getSnapshotBeforeUpdate, the one point that sees exactly the painted DOM of the commit
 * that changes the key: one clone per change, none under StrictMode replays or abandoned transitions.
 */
class Capture extends React.Component<{
  k: unknown;
  on: boolean;
  body: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}> {
  getSnapshotBeforeUpdate(prev: { k: unknown }): Snap {
    const el = this.props.body.current;
    if (!this.props.on || Object.is(prev.k, this.props.k) || !crossFadeOn(el))
      return null;
    if (!hasContent(el)) return "empty";
    return {
      node: snapshotNode(el),
      top: el.offsetTop,
      height: el.offsetHeight,
      scrollTop: el.scrollTop,
    };
  }

  componentDidUpdate(_p: unknown, _s: unknown, snap: Snap) {
    // The body's parent is Swap's outer node; FadeIn's imperative ref is detached during this commit.
    const host = this.props.body.current?.parentElement;
    if (!snap || !host) return;
    if (snap === "empty") replayClass(host, "motion-fade-in");
    else mountGhost(snap.node, host, snap);
  }

  render() {
    return this.props.children;
  }
}

/**
 * FadeIn plus a min-height hold, so the footer does not jump while the new
 * body loads (Dashboard tabs: up to 1,846px).
 */
const Swap: React.FC<Props> = ({
  swapKey,
  crossFade = false,
  holdKey,
  focusHeading = false,
  heading,
  revealId,
  className,
  style,
  children,
  ...rest
}) => {
  const outer = useRef<HTMLElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [hold, setHold] = useState<Hold | null>(null);
  const holdRef = useRef(hold);
  holdRef.current = hold;
  // Held in render, before commit: the old body is still in the DOM, and any
  // child layout read after commit would already clamp the scroll.
  const [shown, setShown] = useState({ swapKey, holdKey });
  if (
    !Object.is(shown.swapKey, swapKey) ||
    !Object.is(shown.holdKey, holdKey)
  ) {
    setShown({ swapKey, holdKey });
    const px = outer.current?.offsetHeight ?? 0;
    if (px > 0) setHold({ px, easing: false });
  }

  useEffect(() => {
    const el = inner.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let timer: number | undefined;
    const settle = () => {
      const h = holdRef.current;
      if (!h) return;
      const content = el.offsetHeight;
      setHold(content >= h.px ? null : { px: content, easing: true });
    };
    const ro = new ResizeObserver(() => {
      if (holdRef.current && !holdRef.current.easing) {
        window.clearTimeout(timer);
        timer = window.setTimeout(settle, motionMs("slow"));
      }
    });
    ro.observe(el);
    if (hold) {
      const done = hold.easing ? () => setHold(null) : settle;
      timer = window.setTimeout(done, motionMs("slow"));
    }
    return () => {
      ro.disconnect();
      window.clearTimeout(timer);
    };
  }, [hold]);

  // A key compare, not a mounted flag: StrictMode re-runs effects on mount.
  const lastKey = useRef(swapKey);
  useEffect(() => {
    if (Object.is(lastKey.current, swapKey)) return;
    lastKey.current = swapKey;
    const strip = revealId ? document.getElementById(revealId) : null;
    const r = strip?.getBoundingClientRect();
    if (strip && r && (r.bottom < 0 || r.top > window.innerHeight)) {
      scrollToAnchor(strip, { block: "nearest", highlight: false });
    }
    const root = outer.current;
    if (!focusHeading || !root) return;
    // Child effects run first: a body that focused its own target (a #row anchor) keeps it.
    const active = document.activeElement;
    if (
      active !== root &&
      root.contains(active) &&
      !active?.closest("[hidden]")
    )
      return;
    const target =
      Array.from(root.querySelectorAll<HTMLElement>("h1, h2, h3")).find(
        (h) => !h.closest("[hidden], .motion-ghost"),
      ) ?? root;
    if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
    target.setAttribute("data-route-focus", "");
    target.focus({ preventScroll: true });
  }, [swapKey, focusHeading, revealId]);

  return (
    <FadeIn
      {...rest}
      ref={outer}
      replayOn={crossFade ? 0 : swapKey}
      className={cn(
        crossFade && "relative",
        hold?.easing && "motion-hold",
        className,
      )}
      style={{ ...style, minHeight: hold ? hold.px : undefined }}
    >
      <Capture k={swapKey} on={crossFade} body={inner}>
        <div ref={inner}>
          {heading && <h2 className="sr-only">{heading}</h2>}
          {children}
        </div>
      </Capture>
    </FadeIn>
  );
};

export default Swap;
